import { useMemo, useState } from 'react'
import { Megaphone, Plus } from 'lucide-react'
import type { Campaign, CampaignStatus, ScheduleItem } from '@/api/client'
import { useCampaigns, useDraftCounts, useSchedule, useTargets } from '@/lib/queries'
import { STATUS } from '@/lib/status'
import { formatShort } from '@/lib/time'
import { Button, ConfirmDialog, EmptyState, ErrorState, PageHeader, Segmented, Skeleton, StatTile, useQueryParam, useToast, useWidePage, errorText } from '@/ui'
import { GenerateNowDialog } from '../review/GenerateNowDialog'
import { channelLabel } from '../schedule/shared'
import { CampaignCard, weekOf } from './CampaignCard'
import { NewCampaignDialog } from './NewCampaignDialog'
import { unslottedLine, useCampaignMutations } from './shared'

type StatusFilter = 'all' | CampaignStatus
const FILTERS: StatusFilter[] = ['all', 'active', 'paused']

/** How far back the cards list posts that went out. */
const POST_DAYS = 30

/** /campaigns: a card per programme — its pace and accounts, its drafts by
 *  state, the last two weeks of posts and the ones that go out next — over
 *  the totals. The table it replaced said how often each campaign posts, not
 *  what it had waiting or what it put up. */
export default function CampaignList() {
  useWidePage()
  const q = useCampaigns()
  const targets = useTargets().data ?? []
  const since = useMemo(() => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), d.getDate() - POST_DAYS).toISOString()
  }, [])
  const feed = useSchedule({ source: 'campaign', from: since, includeUnscheduled: true }, { live: true })
  const review = useDraftCounts({}).data?.counts?.pending_review ?? 0
  const { setStatus } = useCampaignMutations()
  const toast = useToast()
  const [creating, setCreating] = useState(false)
  const [generating, setGenerating] = useState<number | null>(null)
  const [pausing, setPausing] = useState<Campaign | null>(null)
  const [statusParam, setStatusParam] = useQueryParam('status', 'all')
  const status: StatusFilter = (FILTERS as string[]).includes(statusParam) ? (statusParam as StatusFilter) : 'all'

  const toggle = async (c: Campaign) => {
    const next = c.Status === 'active' ? 'paused' : 'active'
    try {
      const r = await setStatus.mutateAsync({ id: c.ID, status: next })
      toast.success(next === 'paused' ? `${c.Name}을 멈췄다` : `${c.Name}을 다시 돌린다.${unslottedLine(r.unslotted)}`)
    } catch (e) {
      toast.error(`바꾸지 못했다: ${errorText(e)}`)
    }
  }

  const items = q.data?.items ?? []
  const posts = feed.data?.items ?? []
  const postsOf = new Map<number, ScheduleItem[]>()
  for (const it of posts) {
    if (!it.campaign) continue
    postsOf.set(it.campaign.id, [...(postsOf.get(it.campaign.id) ?? []), it])
  }
  // Running campaigns first; a paused one keeps its place among the paused.
  const shown = (status === 'all' ? items : items.filter((c) => c.Status === status))
    .slice()
    .sort((a, b) => Number(a.Status !== 'active') - Number(b.Status !== 'active'))
  const countOf = (f: StatusFilter) => (f === 'all' ? items.length : items.filter((c) => c.Status === f).length)

  const now = new Date()
  const running = items.filter((c) => c.Status === 'active')
  const runningIds = new Set(running.map((c) => c.ID))
  const next = posts
    .filter((it) => it.campaign && runningIds.has(it.campaign.id) && it.status === 'scheduled' && it.scheduled_at && Date.parse(it.scheduled_at) > now.getTime())
    .sort((a, b) => String(a.scheduled_at).localeCompare(String(b.scheduled_at)))[0]
  const week = weekOf(posts, now)

  return (
    <>
      <PageHeader
        title="캠페인"
        description="캠페인마다 무엇을 어느 계정으로 얼마나 자주 올릴지 정한다."
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
            새 캠페인
          </Button>
        }
      />
      <div className="space-y-5">
        {items.length > 0 && (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile label="가동 캠페인" value={running.length} hint={`멈춤 ${items.length - running.length} · 전체 ${items.length}`} />
            <StatTile label="검토 대기" value={review} hint={review > 0 ? '승인하면 다음 슬롯에 잡힌다' : '검토할 초안이 없다'} tone={review > 0 ? 'review' : undefined} to="/review" />
            <StatTile
              label="다음 게시"
              value={next?.scheduled_at ? formatShort(next.scheduled_at) : '—'}
              hint={next?.campaign ? `${next.campaign.name} · ${channelLabel(next)}` : '예약된 글 없음'}
            />
            <StatTile
              label="최근 7일 게시"
              value={week.posted}
              hint={week.posted > 0 ? `조회 ${week.views.toLocaleString()} · 좋아요 ${week.likes.toLocaleString()}` : '7일 동안 나간 글이 없다'}
              to="/results"
            />
          </div>
        )}

        {items.length > 0 && (
          <Segmented<StatusFilter>
            label="캠페인 상태"
            value={status}
            onChange={(v) => setStatusParam(v === 'all' ? '' : v)}
            options={FILTERS.map((f) => ({ value: f, label: f === 'all' ? '전체' : STATUS.app[f].label, count: countOf(f) }))}
          />
        )}

        {q.error && !q.data ? (
          <ErrorState title="캠페인을 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} />
        ) : q.isPending ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-80" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Megaphone}
            title="캠페인이 없다"
            description="캠페인은 무엇을 어느 계정으로 얼마나 자주 올릴지 정한다."
            actions={
              <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                새 캠페인
              </Button>
            }
          />
        ) : shown.length === 0 ? (
          <EmptyState icon={Megaphone} title={`${STATUS.app[status]?.label ?? ''} 상태인 캠페인이 없다`} description="위에서 다른 상태를 고른다." />
        ) : (
          <div role="list" aria-label="캠페인 목록" className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {shown.map((c) => (
              <div key={c.ID} role="listitem" className="flex min-w-0">
                <CampaignCard
                  c={c}
                  targets={targets}
                  posts={postsOf.get(c.ID) ?? []}
                  postsLoading={feed.isPending}
                  now={now}
                  onGenerate={() => setGenerating(c.ID)}
                  onToggle={() => (c.Status === 'active' ? setPausing(c) : void toggle(c))}
                  toggling={setStatus.isPending && setStatus.variables?.id === c.ID}
                />
              </div>
            ))}
          </div>
        )}
      </div>
      {creating && <NewCampaignDialog onClose={() => setCreating(false)} />}
      {generating !== null && <GenerateNowDialog open onClose={() => setGenerating(null)} initialCampaignId={generating} />}
      <ConfirmDialog
        open={!!pausing}
        onOpenChange={(o) => !o && setPausing(null)}
        title={`${pausing?.Name ?? ''} 캠페인을 멈출까`}
        description="멈추면 예약된 글도 같이 멈춘다. 예약 시각이 지나도 나가지 않고, 게시 전 알림도 가지 않는다. 재개하면 그동안 시각이 지난 글은 슬롯 없음으로 옮겨진다."
        confirmLabel="멈춤"
        onConfirm={async () => {
          if (pausing) await toggle(pausing)
          setPausing(null)
        }}
      />
    </>
  )
}
